import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '@/lib/auth/authService';
import { useToast } from '@/hooks/use-toast';
import { Lock, Mail, Loader2 } from 'lucide-react';

// Esquema de validación con zod (modificado para permitir tanto usuario como email)
const loginSchema = z.object({
  email: z.string().min(1, 'Este campo es obligatorio'),
  password: z.string().min(1, 'La contraseña es obligatoria'),
});

// Tipo del formulario
type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: (isAdmin: boolean) => void;
  onRegisterClick?: () => void;
  onRecoveryClick?: () => void;
}

export default function LoginForm({ onSuccess, onRegisterClick, onRecoveryClick }: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const { toast } = useToast();

  // Inicializar useForm con el esquema de validación
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Manejar el envío del formulario
  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setCurrentEmail(data.email);
    setEmailNotConfirmed(false);

    try {
      const result = await authService.signIn(data);

      if (result.success) {
        // Obtener los datos del usuario
        const userData = result.data?.user;
        let isAdmin = userData?.user_metadata?.role === 'admin' ||
                      userData?.user_metadata?.role === 'tecnico' ||
                      data.email === 'admin' ||
                      data.email === 'innotec1';

        console.log('Verificación inicial de admin:', {
          userMetadata: userData?.user_metadata,
          isAdmin,
          email: data.email
        });

        // Si el usuario no se detecta como admin por metadatos, consultar la tabla profiles
        if (!isAdmin && userData?.id) {
          console.log('Consultando tabla profiles para usuario ID:', userData.id);

          // Importar supabase directamente desde LoginForm
          const { supabase } = await import('@/lib/supabase');

          // Consultar la tabla profiles para verificar si is_admin = true
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', userData.id)
            .single();

          if (!profileError && profileData && profileData.is_admin === true) {
            console.log('Usuario encontrado como admin en la tabla profiles:', profileData);
            isAdmin = true;

            // Actualizar el userData para reflejar que es admin
            if (userData && userData.user_metadata) {
              userData.user_metadata.is_admin = true;
            }
          } else {
            console.log('Resultado consulta profiles:', { profileData, profileError });
          }
        }

        console.log('Determinación final de admin:', { isAdmin });

        toast({
          title: 'Sesión iniciada',
          description: isAdmin ? 'Accediendo al panel de administración...' : 'Has iniciado sesión correctamente',
          variant: 'default',
        });

        reset();

        // Redirigir a los administradores al panel de administración
        if (isAdmin) {
          // Guardar datos de administrador en localStorage como espera el panel existente
          localStorage.setItem("adminToken", "admin-token");
          localStorage.setItem("adminUser", JSON.stringify({
            username: userData?.email || 'Administrador',
            role: 'admin'
          }));

          // Llamar al callback con el flag isAdmin
          if (onSuccess) onSuccess(true);

          // Redirigir al panel de administración después de un breve retraso
          setTimeout(() => {
            window.location.href = '/admin';
          }, 1000);
        } else {
          // Añadir un retraso para permitir ver los logs y la notificación para usuarios normales
          setTimeout(() => {
            // Llamar a la función de éxito si se proporciona
            if (onSuccess) onSuccess(false);
          }, 1500);
        }
      } else {
        // Comprobar si el error es por email no confirmado
        if (result.message.includes('Email not confirmed')) {
          setEmailNotConfirmed(true);
          toast({
            title: 'Email no confirmado',
            description: 'Por favor, confirma tu correo electrónico para iniciar sesión',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Error',
            description: result.message || 'No se pudo iniciar sesión. Verifica tus credenciales.',
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado. Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Función para reenviar correo de confirmación
  const handleResendConfirmation = async () => {
    if (!currentEmail) return;

    setIsResending(true);

    try {
      const result = await authService.resendConfirmationEmail(currentEmail);

      if (result.success) {
        toast({
          title: 'Correo enviado',
          description: 'Hemos enviado un nuevo correo de confirmación. Por favor, revisa tu bandeja de entrada.',
          variant: 'default',
        });
      } else {
        toast({
          title: 'Error',
          description: result.message || 'No se pudo enviar el correo de confirmación',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Ocurrió un error al enviar el correo de confirmación',
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">Iniciar sesión</h3>
        <p className="text-zinc-400 text-sm">Accede a tu cuenta para ver precios y funciones exclusivas</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="email" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Mail className="w-4 h-4 text-[#FCAA0D]" />
            Email o usuario
          </label>
          <input
            id="email"
            type="text"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="ejemplo@correo.com o usuario"
            {...register('email')}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="text-white/90 flex items-center gap-2 text-sm font-medium">
            <Lock className="w-4 h-4 text-[#FCAA0D]" />
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FCAA0D]/50 focus:border-[#FCAA0D]"
            placeholder="******"
            {...register('password')}
          />
          {errors.password && (
            <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        {emailNotConfirmed && (
          <div className="p-3 bg-amber-900/20 border border-amber-500/20 rounded-lg">
            <p className="text-amber-200 text-sm mb-2">
              Debes confirmar tu correo electrónico antes de iniciar sesión.
            </p>
            <button
              type="button"
              disabled={isResending}
              onClick={handleResendConfirmation}
              className="text-xs text-amber-400 hover:text-amber-300 underline flex items-center gap-1"
            >
              {isResending ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Reenviar correo de confirmación'
              )}
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2 mt-4"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Iniciando sesión...
            </>
          ) : (
            'Iniciar sesión'
          )}
        </button>
      </form>

      <div className="text-center mt-4">
        <p className="text-zinc-500 text-sm">
          ¿No tienes una cuenta?{' '}
          <button
            type="button"
            onClick={onRegisterClick}
            className="text-[#FCAA0D] hover:text-[#FCAA0D]/80 font-medium transition-colors"
          >
            Regístrate aquí
          </button>
        </p>

        {onRecoveryClick && (
          <p className="text-zinc-500 text-sm mt-2">
            ¿Olvidaste tus credenciales?{' '}
            <button
              type="button"
              onClick={onRecoveryClick}
              className="text-[#FCAA0D] hover:text-[#FCAA0D]/80 font-medium transition-colors"
            >
              Recupéralas aquí
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
